import React, { useCallback, useEffect, useState } from "react";
import { fetchAwards } from "../../helpers/awardsApi";
import { formatDate } from "../../helpers/dates";
import { takePurchaseReturn } from "../../helpers/purchaseReturn";

/**
 * Итог покупки: окно, которое встречает человека, вернувшегося с оплаты.
 *
 * Между «оплатил» и «вижу награду» у покупателя не было ни одного
 * подтверждения, что деньги дошли: он входил в кабинет и попадал на обычный
 * экран. Окно закрывает этот разрыв — сколько лицензий куплено, что за них
 * начислено, и кнопка к наградам.
 *
 * Живёт в профиле, а не на главной: в задаче №1908 адресом указан /profile,
 * и это верно по существу — закрыв окно, человек оказывается прямо над
 * списком своих наград, а не на экране, где их нет.
 *
 * Данные — из /v1/birthday/my-awards, той же ручки, что и раздел наград в
 * профиле. Второго источника заводить незачем: покупка и награды по ней —
 * это одна запись заявки, и расходиться им нельзя.
 *
 * Показывается один раз за возвращение, по отметке из sessionStorage, которую
 * ставит страница входа с лендинга.
 *
 * Четыре исхода, и путать их нельзя — человек только что отдал деньги:
 *   награда есть          — покупка оформлена;
 *   заявка ждёт оплаты    — подтверждение от банка ещё не дошло, и если оно
 *                           задерживается, ждать бесполезно, нужна поддержка;
 *   заявки нет вовсе      — мы не видим покупки на эту почту: скорее всего
 *                           платили с другого адреса;
 *   ручка не ответила     — мы просто не знаем, что с оплатой, и врать об
 *                           этом нельзя.
 * Раньше последние три схлопывались в одно «ждите, всё хорошо»: покупатель
 * сидел перед окном, которое обещало награду, которая уже не придёт.
 */


const CloseIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M18 6 6 18M6 6l12 12" />
  </svg>
);

const CheckIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
    <path d="M20 6 9 17l-5-5" />
  </svg>
);

/** «5 лицензий», «2 лицензии» — иначе в окне получается «куплено 2 лицензий». */
const licenseWord = (count) => {
  const n = Math.abs(count) % 100;
  const tail = n % 10;
  if (n > 10 && n < 20) return "лицензий";
  if (tail > 1 && tail < 5) return "лицензии";
  if (tail === 1) return "лицензия";

  return "лицензий";
};

/**
 * Сумма покупки.
 *
 * Ручка my-awards её пока не отдаёт: сумма лежит в заявке (request_lk.amount,
 * в копейках), но в ответ не попадает. Считать её на клиенте по цене лицензии
 * нельзя — цена живёт на сервере (BirthdayController::PRICE) и меняется без
 * нас, а ошибиться в деньгах хуже, чем их не показать. Поэтому строку с
 * суммой рисуем только когда сервер её пришлёт, и молчим, пока не прислал.
 */
const priceLine = (award) => {
  const kopecks = award && award.amount;
  if (typeof kopecks !== "number" || !Number.isFinite(kopecks) || kopecks <= 0) {
    return null;
  }

  return `${(kopecks / 100).toLocaleString("ru-RU")} ₽`;
};

/**
 * Последняя покупка из списка наград.
 *
 * my-awards отдаёт награды по всем оплаченным заявкам, свежая — первой
 * (сортировка по paid_at). Награды одной заявки делят её id, по нему их и
 * собираем: в окне нужен итог того платежа, с которого человек только что
 * вернулся, а не всё, что он покупал за год.
 */
const lastPurchase = (awards) => {
  if (!Array.isArray(awards) || awards.length === 0) return null;

  const requestId = awards[0].id;
  const items = awards.filter((a) => a.id === requestId);
  const license = items.find((a) => a.kind === "license") || items[0];

  return {
    count: Math.max(1, Number(license.count) || 1),
    paidAt: license.paid_at,
    orderId: license.order_id,
    price: priceLine(license),
    items,
  };
};

/**
 * Заявка, по которой оплата ещё не подтвердилась.
 *
 * Берём самую свежую: человек вернулся с одного платежа, и говорить надо про
 * него. Порядок задаёт сервер (по created_at, свежая первой).
 *
 * known отделяет «сервер сказал, что заявок нет» от «сервер про заявки вообще
 * не ответил». Поле pending_payments появилось позже самой ручки, и на не
 * обновлённом контуре его не будет. Считать его отсутствие за «покупки нет»
 * нельзя: мы бы сказали заплатившему человеку, что не видим его денег, хотя
 * на деле просто не спрашивали.
 */
const waitingPayment = (list) => {
  if (!Array.isArray(list)) return { known: false, payment: null };

  return { known: true, payment: list.length > 0 ? list[0] : null };
};

/**
 * Пора ли звать поддержку.
 *
 * Уведомление от эквайера доходит за секунды, изредка за минуты. Если прошло
 * заметно больше, само оно уже не дойдёт: повторной сверки с ЮKassa на
 * платформе нет, заявка так и останется неоплаченной. Статус review — тот же
 * тупик, только явный: уведомление пришло на платёж, которого мы не выставляли,
 * и разобрать его может только человек.
 */
const STUCK_MINUTES = 15;

const isStuck = (payment) =>
  Boolean(payment) &&
  (payment.needs_support === true ||
    payment.status === "review" ||
    (typeof payment.waiting_minutes === "number" && payment.waiting_minutes >= STUCK_MINUTES));

/** Номер, который поддержка спросит первым: заказ, а если его нет — платёж. */
const paymentRef = (payment) =>
  (payment && (payment.order_id || payment.payment_id)) || "";

const PurchaseSummary = () => {
  // Отметку снимаем сразу при монтировании: если человек уйдёт со страницы,
  // пока грузятся награды, окно не должно всплыть на следующем экране.
  const [asked] = useState(takePurchaseReturn);
  const [open, setOpen] = useState(asked);
  const [purchase, setPurchase] = useState(null);
  const [pending, setPending] = useState({ known: false, payment: null });
  const [loading, setLoading] = useState(asked);
  const [error, setError] = useState("");

  // isAlive — чтобы после ухода со страницы не дописывать состояние в
  // размонтированное окно: ответ ручки приходит уже после закрытия.
  const load = useCallback((isAlive = () => true) => {
    setLoading(true);
    setError("");

    return fetchAwards()
      .then((data) => {
        if (!isAlive()) return;

        setPurchase(lastPurchase(data && data.awards));
        setPending(waitingPayment(data && data.pending_payments));
      })
      .catch((e) => {
        if (isAlive()) {
          setError(e && e.message ? e.message : "не удалось получить данные о покупке");
        }
      })
      .finally(() => {
        if (isAlive()) setLoading(false);
      });
  }, []);

  useEffect(() => {
    if (!asked) return undefined;

    let alive = true;
    load(() => alive);

    return () => {
      alive = false;
    };
  }, [asked, load]);

  /* Одна автоматическая перепроверка, если оплата ещё не подтверждена.
     Уведомление от эквайера обычно доходит за секунды, но человек попадает
     в кабинет раньше — и видит «оплата подтверждается» с кнопкой, которую
     надо жать самому. Один повтор закрывает большинство таких случаев;
     дальше уже честнее показать кнопку, чем опрашивать сервер по кругу и
     держать человека перед крутящимся окном.

     Заявку, которая уже застряла, не перепроверяем: сама она не сдвинется,
     и повтор лишь оттянет момент, когда человек пойдёт в поддержку. */
  const [retried, setRetried] = useState(false);
  useEffect(() => {
    if (!open || loading || error || purchase || retried || isStuck(pending.payment)) return undefined;

    const t = setTimeout(() => {
      setRetried(true);
      load();
    }, 5000);

    return () => clearTimeout(t);
  }, [open, loading, error, purchase, pending, retried, load]);

  // Закрытие по Esc: окно перекрывает профиль, и выход по клавише ожидаем.
  useEffect(() => {
    if (!open) return undefined;

    const onKey = (e) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("keydown", onKey);

    return () => document.removeEventListener("keydown", onKey);
  }, [open]);

  if (!open) return null;

  return (
    <div className="fw-modal-back" onClick={() => setOpen(false)} role="presentation">
      <div
        className="fw-modal fw-sum"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label="Итог покупки"
      >
        <button type="button" className="fw-modal-close" onClick={() => setOpen(false)} aria-label="Закрыть">
          <CloseIcon />
        </button>

        <div className="fw-sum-body">
          {loading && <p className="fw-sec-sub mb-0">Проверяем оплату…</p>}

          {/* Ручка не ответила — про оплату мы не знаем ничего. Обещать здесь
              «награды придут сами» нельзя: если сломан сервер, не придут. */}
          {!loading && error && (
            <>
              <h3>Не удалось проверить оплату</h3>
              <p className="fw-modal-desc">
                {error}. Мы не смогли узнать у платформы, дошли ли деньги, —
                это не значит, что они потерялись, но и подтвердить покупку мы
                сейчас не можем. Попробуйте ещё раз, а если не поможет —
                напишите в поддержку и назовите номер платежа из письма или
                чека от банка.
              </p>
              <div className="fw-sum-acts">
                <button type="button" className="fw-btn" onClick={() => load()}>
                  Проверить ещё раз
                </button>
                <button type="button" className="fw-btn ghost" onClick={() => setOpen(false)}>
                  Закрыть
                </button>
              </div>
            </>
          )}

          {/* Заявка есть, оплата по ней не подтверждена. В первые минуты это
              обычное дело, дальше — застрявший платёж: досверки с эквайером на
              платформе нет, и сам он в оплаченные уже не перейдёт. Поэтому
              после ожидания говорим не «ждите», а «идите в поддержку». */}
          {!loading && !error && !purchase && (pending.payment || !pending.known) && (
            <>
              <h3>{isStuck(pending.payment) ? "Оплата не подтвердилась" : "Оплата ещё подтверждается"}</h3>
              <p className="fw-modal-desc">
                {isStuck(pending.payment) ? (
                  <>
                    Заказ мы видим, а подтверждения оплаты от банка так и не
                    получили — само оно уже не придёт. Напишите в поддержку и
                    назовите номер
                    {paymentRef(pending.payment) ? ` ${paymentRef(pending.payment)}` : " заказа"}: по
                    нему платёж сверят с банком и выдадут награды вручную.
                  </>
                ) : (
                  <>
                    Банк сообщает нам об оплате не мгновенно — обычно это
                    занимает до нескольких минут.{" "}
                    {retried ? "Мы уже проверили ещё раз." : "Проверим ещё раз через несколько секунд."}{" "}
                    Награды появятся в профиле сами. Если через четверть часа их
                    не будет, напишите в поддержку и назовите номер
                    {paymentRef(pending.payment) ? ` ${paymentRef(pending.payment)}` : " платежа из чека банка"}.
                  </>
                )}
              </p>

              {paymentRef(pending.payment) ? (
                <dl className="fw-modal-props">
                  <dt>Заказ</dt>
                  <dd>{paymentRef(pending.payment)}</dd>
                </dl>
              ) : null}

              <div className="fw-sum-acts">
                <button type="button" className="fw-btn" onClick={() => load()}>
                  Проверить ещё раз
                </button>
                <button type="button" className="fw-btn ghost" onClick={() => setOpen(false)}>
                  Закрыть
                </button>
              </div>
            </>
          )}

          {/* Ни награды, ни заявки — и это сказал сам сервер. Значит покупку на
              эту почту платформа не видит вовсе: чаще всего платили с одного
              адреса, а вошли под другим. Прямо об этом и говорим: догадаться
              сам человек не может, а поддержка по номеру платежа находит заказ
              за минуту. */}
          {!loading && !error && !purchase && pending.known && !pending.payment && (
            <>
              <h3>Мы не нашли оплату на эту почту</h3>
              <p className="fw-modal-desc">
                На этот адрес у нас нет ни одной покупки — ни оплаченной, ни
                ожидающей подтверждения. Если деньги списаны, скорее всего при
                оплате была указана другая почта: награды приходят именно на
                неё, войдите под тем адресом. Если адрес тот же — напишите в
                поддержку и назовите номер платежа из письма или чека от банка,
                мы найдём заказ и выдадим награды.
              </p>
              <div className="fw-sum-acts">
                <button type="button" className="fw-btn" onClick={() => load()}>
                  Проверить ещё раз
                </button>
                <button type="button" className="fw-btn ghost" onClick={() => setOpen(false)}>
                  Закрыть
                </button>
              </div>
            </>
          )}

          {!loading && !error && purchase && (
            <>
              <span className="fw-sum-ic">
                <CheckIcon />
              </span>
              <h3>Спасибо, покупка оформлена</h3>
              <p className="fw-modal-desc">
                {purchase.count} {licenseWord(purchase.count)} «Волшебный мир — 5 лет»
                {purchase.paidAt ? `, оплачено ${formatDate(purchase.paidAt)}` : ""}.
              </p>

              <dl className="fw-modal-props">
                <dt>Лицензий</dt>
                <dd>{purchase.count} шт.</dd>

                {purchase.price ? (
                  <>
                    <dt>Сумма</dt>
                    <dd>{purchase.price}</dd>
                  </>
                ) : null}

                {purchase.orderId ? (
                  <>
                    <dt>Заказ</dt>
                    <dd>{purchase.orderId}</dd>
                  </>
                ) : null}
              </dl>

              <div className="fw-sec-head">
                <h2>Начислено</h2>
              </div>
              <ul className="fw-sum-list">
                {purchase.items.map((a) => (
                  <li key={a.key}>
                    <b>
                      {a.title}
                      {a.count > 1 ? ` — ${a.count} шт.` : ""}
                    </b>
                    {a.status === "active" ? (
                      <span className="fw-tag ok">
                        <CheckIcon />
                        Активна
                      </span>
                    ) : (
                      <span className="fw-tag wait">Ждёт активации</span>
                    )}
                  </li>
                ))}
              </ul>

              {/* Не ссылка на профиль: окно и так открыто поверх него.
                  Закрыв его, человек оказывается прямо над своими наградами. */}
              <div className="fw-sum-acts">
                <button type="button" className="fw-btn" onClick={() => setOpen(false)}>
                  Смотреть награды
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default PurchaseSummary;
